import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Outlet } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'antd/dist/reset.css';
import { ConfigProvider, theme } from 'antd';
import { initVersionCheck } from './services/versionCheck';
import { ThemeProvider, useTheme } from './context/ThemeContext';

// Auth Components
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import DriverRunsDetail from './pages/dailyRuns/DriverRunsDetail';

// Layout Components
import MainLayout from './layouts/MainLayout';

// Job Management
import JobList from './pages/jobs/JobList';
import JobCreate from './pages/jobs/JobCreate';
import JobEdit from './pages/jobs/JobEdit';
import JobDetail from './pages/jobs/JobDetail';

// Driver Management
import DriverList from './pages/drivers/DriverList';
import DriverCreate from './pages/drivers/DriverCreate';
import DriverEdit from './pages/drivers/DriverEdit';
import DriverDetail from './pages/drivers/DriverDetail';

// Van Management
import VanList from './pages/vans/VanList';
import VanCreate from './pages/vans/VanCreate';
import VanEdit from './pages/vans/VanEdit';
import VanDetail from './pages/vans/VanDetail';

// Payment Management
import PaymentList from './pages/payments/PaymentList';
import PaymentDetail from './pages/payments/PaymentDetail';
import PaymentCreate from './pages/payments/PaymentCreate';
import PaymentEdit from './pages/payments/PaymentEdit';
import OverduePayments from './pages/payments/OverduePayments';

// Reports
import ReportDashboard from './pages/reports/ReportDashboard';
import FinancialReport from './pages/reports/FinancialReport';
import RevenueReport from './pages/reports/RevenueReport';
import DriverReports from './pages/reports/DriverReports';
import VanReport from './pages/reports/VanReport';
import JobMap from './pages/reports/JobMap';
import RouteCorridor from './pages/reports/RouteCorridor';
import DemandByDay from './pages/reports/DemandByDay';
import MileageCost from './pages/reports/MileageCost';

// Utility Pages
import ServerStatus from './pages/ServerStatus';

// Utils
import PrivateRoute from './components/PrivateRoute';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Navigate } from 'react-router-dom';
import ChannelList from './pages/channels/ChannelList';

// Invoice Management
import InvoiceList from './pages/invoices/InvoiceList';
import InvoiceCreate from './pages/invoices/InvoiceCreate';
import InvoiceView from './pages/invoices/InvoiceView';

// Reconciliation
import Reconciliation from './pages/reconciliation/Reconciliation';

// Agent
import AgentLogs from './pages/agent/AgentLogs';

// Enquiries
import EnquiryList from './pages/enquiries/EnquiryList';

// Daily Contract Runs
import DailyRunList from './pages/dailyRuns/DailyRunList';
import FuelRunList from './pages/fuelRuns/FuelRunList';

import DailyRunOverview from './pages/dailyRuns/DailyRunOverview';
import DailyRunBreakdown from './pages/dailyRuns/DailyRunBreakdown';
import OpcDrivers from './pages/dailyRuns/OpcDrivers';
import OpcVans from './pages/dailyRuns/OpcVans';
import OpcClients from './pages/dailyRuns/OpcClients';
import DriverWages from './pages/dailyRuns/DriverWages';
import ExternalDrivers from './pages/dailyRuns/ExternalDrivers';
import ExternalDriverJobs from './pages/dailyRuns/ExternalDriverJobs';
import OpcInvoices from './pages/dailyRuns/OpcInvoices';

// POD
import PodList from './pages/pods/PodList';

// Expenses
import Expenses from './pages/expenses/Expenses';

const OrgOnlyRoute = () => {
  const { isOpcUser } = useAuth();
  return isOpcUser ? <Navigate to="/daily-runs" replace /> : <Outlet />;
};

const DefaultRedirect = () => {
  const { isOpcUser } = useAuth();
  return <Navigate to={isOpcUser ? '/daily-runs' : '/jobs'} replace />;
};

const AppWithTheme = ({ children }) => {
  const { isDarkMode } = useTheme();
  return (
    <ConfigProvider theme={{
      algorithm: isDarkMode ? theme.darkAlgorithm : theme.defaultAlgorithm,
      token: { colorPrimary: isDarkMode ? '#4096ff' : '#1677ff' },
    }}>
      {children}
    </ConfigProvider>
  );
};

const App = () => {
  useEffect(() => {
    initVersionCheck();
  }, []);

  return (
    <ThemeProvider>
      <AppWithTheme>
      <AuthProvider>
      <Router>
        <ToastContainer position="top-right" autoClose={3000} />
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/server-status" element={<ServerStatus />} />
          
          {/* Protected Routes */}
          <Route element={<PrivateRoute />}>
            <Route element={<MainLayout />}>
              {/* Default redirect */}
              <Route path="/" element={<DefaultRedirect />} />

              {/* Daily Runs — accessible to all users including @opc.com */}
              <Route path="/daily-runs" element={<DailyRunList />} />

              <Route path="/daily-runs/overview" element={<DailyRunOverview />} />
              <Route path="/daily-runs/breakdown" element={<DailyRunBreakdown />} />
              <Route path="/daily-runs/driver-detail" element={<DriverRunsDetail />} />
              <Route path="/daily-runs/drivers" element={<OpcDrivers />} />
              <Route path="/daily-runs/vans" element={<OpcVans />} />
              <Route path="/daily-runs/clients" element={<OpcClients />} />
              <Route path="/daily-runs/wages" element={<DriverWages />} />
              <Route path="/daily-runs/external-drivers" element={<ExternalDrivers />} />
              <Route path="/daily-runs/external-drivers/:driverId" element={<ExternalDriverJobs />} />
              <Route path="/daily-runs/invoices" element={<OpcInvoices />} />
              <Route path="/fuel-runs" element={<FuelRunList />} />
              <Route path="/expenses" element={<Expenses />} />

              {/* Org-only routes — @opc.com users are redirected away */}
              <Route element={<OrgOnlyRoute />}>
                {/* PODs — our data, not for OPC */}
                <Route path="/pods" element={<PodList />} />

                {/* Jobs */}
                <Route path="/jobs" element={<JobList />} />
                <Route path="/jobs/create" element={<JobCreate />} />
                <Route path="/jobs/edit/:id" element={<JobEdit />} />
                <Route path="/jobs/:id" element={<JobDetail />} />

                {/* Drivers */}
                <Route path="/drivers" element={<DriverList />} />
                <Route path="/drivers/create" element={<DriverCreate />} />
                <Route path="/drivers/edit/:id" element={<DriverEdit />} />
                <Route path="/drivers/:id" element={<DriverDetail />} />

                {/* Vans */}
                <Route path="/vans" element={<VanList />} />
                <Route path="/vans/create" element={<VanCreate />} />
                <Route path="/vans/edit/:id" element={<VanEdit />} />
                <Route path="/vans/:id" element={<VanDetail />} />

                {/* Payments */}
                <Route path="/payments" element={<PaymentList />} />
                <Route path="/payments/overdue" element={<OverduePayments />} />
                <Route path="/payments/create" element={<PaymentCreate />} />
                <Route path="/payments/edit/:id" element={<PaymentEdit />} />
                <Route path="/payments/:id" element={<PaymentDetail />} />

                {/* Channels */}
                <Route path="/channels" element={<ChannelList />} />

                {/* Invoices */}
                <Route path="/invoices" element={<InvoiceList />} />
                <Route path="/invoices/create" element={<InvoiceCreate />} />
                <Route path="/invoices/:id" element={<InvoiceView />} />

                {/* Reconciliation */}
                <Route path="/reconciliation" element={<Reconciliation />} />

                {/* Reports */}
                <Route path="/reports" element={<ReportDashboard />} />
                <Route path="/reports/financial" element={<FinancialReport />} />
                <Route path="/reports/revenue" element={<RevenueReport />} />
                <Route path="/reports/driver" element={<DriverReports />} />
                <Route path="/reports/van" element={<VanReport />} />
                <Route path="/reports/job-map" element={<JobMap />} />
                <Route path="/reports/corridors" element={<RouteCorridor />} />
                <Route path="/reports/demand" element={<DemandByDay />} />
                <Route path="/reports/mileage-cost" element={<MileageCost />} />

                <Route path="/agent-logs" element={<AgentLogs />} />
                <Route path="/enquiries" element={<EnquiryList />} />
              </Route>
            </Route>
          </Route>
        </Routes>
      </Router>
    </AuthProvider>
    </AppWithTheme>
    </ThemeProvider>
  );
};

export default App;